import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { ShoppingBasket, Menu, Globe, MapPin } from "lucide-react";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const Header = () => {
  const { t, lang, setLang } = useLang();
  const { count, setDrawerOpen } = useCart();
  const location = useLocation();

  const navItems = [
    { key: "home", href: "/" },
    { key: "restaurants", href: "/restaurants" },
    { key: "categories", href: "/categories" },
    { key: "familyDeals", href: "/family-deals" },
    { key: "howItWorks", href: "/how-it-works" },
    { key: "help", href: "/help" },
  ];

  const langNames = { en: "EN", it: "IT", es: "ES" };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-navy/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <div className="w-9 h-9 rounded-xl bg-coral flex items-center justify-center text-white font-bold text-lg">CE</div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading font-extrabold text-navy text-lg tracking-tight">CALETA EATS</span>
            <span className="text-[10px] text-navy/60 hidden sm:block flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> Caleta de Fuste</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((n) => (
            <Link
              key={n.key}
              to={n.href}
              data-testid={`nav-${n.key}`}
              className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${location.pathname === n.href ? "bg-navy text-white" : "text-navy hover:bg-sand-100"}`}
            >
              {t(n.key)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="rounded-full h-9 px-2 sm:px-3" data-testid="lang-switcher">
                <Globe className="w-4 h-4 mr-1" />
                <span className="text-xs font-semibold">{langNames[lang]}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">🇬🇧 English</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("it")} data-testid="lang-it">🇮🇹 Italiano</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("es")} data-testid="lang-es">🇪🇸 Español</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={() => setDrawerOpen(true)}
            className="relative rounded-full p-2 hover:bg-sand-100 transition-colors"
            data-testid="header-basket-btn"
            aria-label="Basket"
          >
            <ShoppingBasket className="w-5 h-5 text-navy" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-coral text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="basket-count">
                {count}
              </span>
            )}
          </button>

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader><SheetTitle className="font-heading text-navy">Menu</SheetTitle></SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {navItems.map((n) => (
                  <Link
                    key={n.key}
                    to={n.href}
                    data-testid={`mobile-nav-${n.key}`}
                    className="px-3 py-3 rounded-lg text-navy hover:bg-sand-100 font-medium"
                  >
                    {t(n.key)}
                  </Link>
                ))}
                <Link to="/partner" className="px-3 py-3 rounded-lg text-navy hover:bg-sand-100 font-medium" data-testid="mobile-nav-partner">{t("partner")}</Link>
                <Link to="/admin" className="px-3 py-3 rounded-lg text-navy/60 hover:bg-sand-100 text-sm mt-4" data-testid="mobile-nav-admin">{t("admin")}</Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
